export const DUE_DILIGENCE_ITEMS = [
    {
        key: 'legal_status',
        label: 'Legal registration confirmed',
        detail: 'NPO certificate, CIPC registration (NPC) or trust deed and Master’s letter of authority. The registered name matches the applicant.',
    },
    {
        key: 'dsd_status',
        label: 'DSD compliance status',
        detail: 'Registered and compliant on the DSD NPO register, not lapsed or in the process of deregistration.',
        // Registration with DSD is voluntary for many NPCs and trusts.
        naAllowed: true,
    },
    {
        key: 'cipc_status',
        label: 'CIPC status (if an NPC)',
        detail: '“In Business”, not “AR Deregistration” or finally deregistered; annual returns up to date.',
        naAllowed: true,
    },
    {
        key: 'tax_pbo',
        label: 'SARS PBO / 18A approval',
        detail: 'PBO approval letter and, if donations are receipted, s18A approval and exemption reference number.',
        naAllowed: true,
    },
    {
        key: 'tax_clearance',
        label: 'Tax compliance status',
        detail: 'A current TCS PIN that verifies as compliant on eFiling. A PIN that was once issued is not enough.',
        referenceLabel: 'TCS PIN',
        expires: true,
    },
    {
        key: 'afs',
        label: 'Financial statements reviewed',
        detail: 'Latest AFS (ideally under 15 months old) with the accounting officer, independent review or audit opinion, and no going-concern or qualification red flags.',
    },
    {
        key: 'bank',
        label: 'Bank account in the legal name',
        detail: 'A bank confirmation letter in the organisation’s registered name (not a personal or trading-name account), matching the payee details.',
        expires: true,
    },
    {
        key: 'governance',
        label: 'Board and governance',
        detail: 'At least three unrelated board members, none an employee, a recent AGM, and no disqualified office-bearer (GLAA / Companies Act).',
    },
    {
        // Hijacked NPOs and changed signatories are how grant money goes
        // missing: the registration is real and the people are not. Checking
        // the names on the certificate against the people asking for the money,
        // and screening them, is what a funder's verification pack asks for.
        key: 'office_bearers',
        label: 'Office bearers identified and screened',
        detail: 'ID documents for the chairperson, treasurer and bank signatories match the DSD, CIPC and bank records, and their names have been checked against the FIC targeted financial sanctions list.',
    },
    {
        key: 'conflict',
        label: 'Conflict of interest declared',
        detail: 'No undisclosed relationship between the applicant and the fund’s staff, trustees or connected parties.',
    },
    {
        key: 'related_party',
        label: 'Related-party and other funding',
        detail: 'Other funders of the same project disclosed (double-funding is the most common audit finding), and any related-party transactions understood.',
    },
    {
        key: 'safeguarding',
        label: 'Safeguarding and police clearance',
        detail: 'A child-protection / safeguarding policy and police clearances for staff working with children, plus checks against the National Register for Sex Offenders where applicable.',
        safeguarding: true,
        naAllowed: true,
    },
    {
        key: 'references',
        label: 'References and site visit',
        detail: 'At least one reference from a previous funder or partner and, for a larger grant, a site visit report.',
    },
];
export const DILIGENCE_STATUS_LABELS = {
    pending: 'Not checked',
    verified: 'Verified',
    flag: 'Concern',
    na: 'Not applicable',
};
/**
 * The status an item counts as. "Not applicable" on an item that always
 * applies is not an answer, and verified evidence past its expiry date is
 * not verified any more.
 */
function effectiveStatus(item, rec, today) {
    const status = rec?.status ?? 'pending';
    if (status === 'na' && !item.naAllowed)
        return 'pending';
    if (status === 'verified' && today && rec?.expiresAt && rec.expiresAt < today)
        return 'pending';
    return status;
}
/**
 * Completeness score: verified items as a percentage of those that
 * apply (n/a items excluded). A single flag is surfaced separately —
 * one real concern matters more than a high percentage.
 *
 * `today` (yyyy-mm-dd, Africa/Johannesburg) makes expired evidence count
 * as not yet verified; leave it out and expiry is ignored.
 */
export function diligenceScore(items, today) {
    const values = DUE_DILIGENCE_ITEMS.map((i) => effectiveStatus(i, items[i.key], today));
    const applicable = values.filter((s) => s !== 'na').length;
    const verified = values.filter((s) => s === 'verified').length;
    const flags = values.filter((s) => s === 'flag').length;
    return { applicable, verified, flags, pct: applicable > 0 ? Math.round((verified / applicable) * 100) : 0 };
}
/**
 * Whether a board may approve on this diligence without minuting a reason.
 *
 * The board page showed a percentage beside the score and the decision
 * callable never looked at it, so an approval could be minuted for an
 * applicant nobody had checked at all. This is the test the decision
 * applies, and the labels are what the board is told is missing.
 */
export function diligenceGate(items, today) {
    const outstanding = [];
    const flagged = [];
    const expired = [];
    for (const item of DUE_DILIGENCE_ITEMS) {
        const rec = items[item.key];
        const status = effectiveStatus(item, rec, today);
        if (status === 'flag')
            flagged.push(item.label);
        else if (status === 'pending') {
            if (rec?.status === 'verified')
                expired.push(item.label);
            else
                outstanding.push(item.label);
        }
    }
    const score = diligenceScore(items, today);
    return {
        complete: outstanding.length === 0 && flagged.length === 0 && expired.length === 0,
        verified: score.verified,
        applicable: score.applicable,
        outstanding,
        flagged,
        expired,
    };
}
